import { defineComponent as _defineComponent } from 'vue'
import {
  ref,
  computed,
  watch,
  inject,
  getCurrentInstance,
  onMounted,
  onUnmounted,
} from "vue";
import {
  detectCardType,
  cardMaskByType,
  buildMaskedCardNumber,
} from "@/utils/creditCard";
import { Address } from "@/models/dto";
import { FormattedPaymentMethod } from "../models/FormattedPaymentMethod";
import AddressPicker from "@/components/AddressPicker.vue";
import { finixConfig } from "@/utils/env";


export default /*#__PURE__*/_defineComponent({
  __name: 'CreditCardFormV2',
  props: {
    showCancel: { type: Boolean },
    disputedCheckout: { type: Boolean },
    value: null,
    hideSaveForLater: { type: Boolean }
  },
  emits: ["cancel", "input", "update:form-errors"],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  showCancel?: boolean;
  disputedCheckout?: boolean;
  value?: FormattedPaymentMethod;
  hideSaveForLater?: boolean;
};






const isInGuestCheckout = inject("isInGuestCheckout", false);

const cardFormRef = ref();
const addressFormRef = ref();
const addressPickerRef = ref();

const nameOnCard = ref("");
const cardNumber = ref("");
const expiration = ref("");
const cvv = ref("");
const saveCard = ref(false);
const address = ref<Partial<Address>>({});

const { proxy } = getCurrentInstance()!;
const $t = proxy.$t.bind(proxy);

const finixForm = ref<any>(null);

const cardNumberMask = computed(() => {
  if (props.disputedCheckout) {
    return null;
  }
  return cardMaskByType(cardType.value);
});

const rawCardNumber = computed(() => cardNumber.value.replace(/\D/g, ""));

const cardType = computed(() => detectCardType(rawCardNumber.value));

const cardIconName = computed(() => {
  if (cardType.value === "default") {
    return "default_card";
  }
  return cardType.value;
});

const lastFourDigits = computed(() =>
  rawCardNumber.value.substr(rawCardNumber.value.length - 4, 4)
);

const formatData = (): FormattedPaymentMethod => {
  const {
    addressName,
    street1,
    street2,
    city,
    state,
    postalCode,
    lat,
    lng,
    title,
    timeZone,
    country,
  } = address.value;

  return {
    activeMethod: "credit_card",
    showOnCharterUp: saveCard.value,
    name: nameOnCard.value,
    cardholderName: nameOnCard.value,
    cardNumber: rawCardNumber.value,
    mask: lastFourDigits.value,
    securityCode: cvv.value,
    exp_date: expiration.value,
    expirationMonth: expiration.value?.split("/")?.[0] || "",
    expirationYear: expiration.value?.split("/")?.[1] || "",
    type_label: cardType.value,
    address: {
      name: addressName,
      street1,
      street2,
      city,
      state,
      postalCode,
      lat,
      lng,
      title,
      time_zone: timeZone,
      country,
    },
  };
};

const formattedData = computed<FormattedPaymentMethod>(() => formatData());

const expirationRules = [
  (v: string) => !!v || $t("creditCardForm.EXPIRATION.RULES.REQUIRED"),
  (v: string) =>
    /^(0[1-9]|1[0-2])\/(\d{2}|\d{4})$/.test(v) ||
    $t("creditCardForm.EXPIRATION.RULES.VALID"),
  (v: string) =>
    isDateInFuture(v) || $t("creditCardForm.EXPIRATION.RULES.FUTURE"),
];

watch(
  () => props.value,
  (newValue) => {
    if (!props.disputedCheckout) return;
    if (!newValue) return;
    nameOnCard.value = newValue?.name || newValue?.cardholderName || "";
    cardNumber.value =
      buildMaskedCardNumber(newValue?.mask, newValue?.type_label) || "";
    expiration.value = newValue?.exp_date || "";
    cvv.value = newValue?.securityCode || "";
    address.value = {
      name: newValue?.address?.name || "",
      street1: newValue?.address?.street1 || "",
      street2: newValue?.address?.street2 || "",
      city: newValue?.address?.city || "",
      state: newValue?.address?.state || "",
      // @ts-ignore
      postalCode:
        newValue?.address?.postalCode || newValue?.address?.postal_code || "",
      title: newValue?.address?.title || "",
      country: newValue?.address?.country || "",
    };
  },
  { immediate: true }
);

watch(formattedData, (val) => {
  if (props.disputedCheckout) return;
  emit("input", val);
});

const finixErrors = ref<string[]>([]);
const addressFormValid = ref(false);
const cardFormValid = ref(false);

const emitFormErrors = () => {
  const hasErrors =
    finixErrors.value.length > 0 ||
    !cardFormValid.value ||
    !addressFormValid.value;
  emit("update:form-errors", hasErrors);
};

const updateFinixErrors = () => {
  if (finixForm.value && finixForm.value.state) {
    const errors: string[] = [];
    Object.values(finixForm.value.state).forEach((field: any) => {
      if (
        Array.isArray(field.errorMessages) &&
        field.errorMessages.length > 0
      ) {
        errors.push(...field.errorMessages);
      }
    });
    finixErrors.value = errors;
    emitFormErrors();
  }
};

watch(addressFormValid, emitFormErrors);
watch(cardFormValid, emitFormErrors);

let finixErrorInterval: number | undefined;

onMounted(() => {
  initFinixForm();
  if (!props.disputedCheckout) {
    finixErrorInterval = window.setInterval(updateFinixErrors, 1000);
  }
});

onUnmounted(() => {
  if (finixErrorInterval) clearInterval(finixErrorInterval);
});

const initFinixForm = async () => {
  const windowWithFinix = window as any;
  if (!windowWithFinix.Finix) {
    console.warn("Failed to load Finix. Attempting to reload...");
    await proxy.$loadScript("https://js.finix.com/v/1/finix.js");
  }
  finixForm.value = windowWithFinix.Finix.CardTokenForm(
    "finix-form",
    finixOptions
  );
};

const getTokenizedFinixInfo = async (): Promise<string> => {
  const { environment, applicationId } = finixConfig();
  return new Promise((resolve, reject) => {
    finixForm.value.submit(environment, applicationId, function (err, res) {
      if (err) {
        reject(err);
      } else {
        const tokenData = res.data || {};
        const token = tokenData.id;
        resolve(token);
      }
    });
  });
};

const isDateInFuture = (expirationDate: string): boolean => {
  const [month, year] = expirationDate.split("/").map(Number);
  if (isNaN(month) || isNaN(year)) {
    return false;
  }
  let yearString = year.toString();
  if (yearString.length === 2) {
    yearString = `20${year}`;
  }
  const lastDayOfMonth = proxy
    .$dayjs(`${yearString}-${month.toString().padStart(2, "0")}`)
    .endOf("month");
  const currentDate = proxy.$dayjs().startOf("day");
  return lastDayOfMonth.isSameOrAfter(currentDate);
};

const cancel = () => {
  emit("cancel");
  reset();
};

const reset = () => {
  nameOnCard.value = "";
  cardNumber.value = "";
  expiration.value = "";
  cvv.value = "";
  saveCard.value = false;
  address.value = {};
  const addressPicker = addressPickerRef.value as any;
  if (addressPicker && addressPicker.clearAddress) {
    addressPicker.clearAddress();
  }
};

const validate = (): boolean => {
  const cardForm = cardFormRef.value as any;
  const cardFormValid = cardForm.validate();
  const addressForm = addressFormRef.value as any;
  const addressFormValid = addressForm.validate();
  let firstInvalidInput = null;
  cardForm.inputs.some((input: any) => {
    if (!input.valid && !firstInvalidInput) {
      firstInvalidInput = input;
    }
    return !input.valid;
  });
  addressForm.inputs.some((input: any) => {
    if (!input.valid && !firstInvalidInput) {
      firstInvalidInput = input;
    }
    return !input.valid;
  });
  if (firstInvalidInput) {
    proxy.$vuetify.goTo(firstInvalidInput.$el.offsetTop);
  }
  return addressFormValid && cardFormValid;
};

const finixOptions = {
  showAddress: false,
  showLabels: true,
  labels: {
    name: "Name",
    security_code: "Security Code",
    address_line1: "Address",
  },
  showPlaceholders: false,
  hideFields: [
    "address_line1",
    "address_line2",
    "address_state",
    "address_city",
    "address_postal_code",
    "address_region",
    "address_country",
  ],
  requiredFields: ["name"],
  hideErrorMessages: false,
  errorMessages: {
    name: "Name is required",
    number: "Card number is required",
    security_code: "Security code is required",
  },
  styles: {
    default: {
      color: "#3c4853",
      border: "1px solid #a0aeba",
      borderRadius: "5px",
      padding: "12px",
      fontFamily: "'Inter Bold', Arial, sans-serif",
      fontWeight: "normal",
      fontSize: "16px",
      overflow: "hidden",
      height: "42px",
    },
    error: {
      color: "#3c4853",
    },
  },
};

expose({
  getTokenizedFinixInfo,
  validate,
  reset,
  "card-form": cardFormRef,
  "address-form": addressFormRef,
});

return { __sfc: true,props, emit, isInGuestCheckout, cardFormRef, addressFormRef, addressPickerRef, nameOnCard, cardNumber, expiration, cvv, saveCard, address, proxy, $t, finixForm, cardNumberMask, rawCardNumber, cardType, cardIconName, lastFourDigits, formatData, formattedData, expirationRules, finixErrors, addressFormValid, cardFormValid, emitFormErrors, updateFinixErrors, finixErrorInterval, initFinixForm, getTokenizedFinixInfo, isDateInFuture, cancel, reset, validate, finixOptions, AddressPicker }
}

})